// src/components/ParcelMonitor.js
// Surveillance des colis — /parcel-monitor. Every tracked parcel scored by
// the risk models; an alert opens on a parcel at risk and a person takes it
// into account or resolves it WITH a reason (that reason measures the models'
// precision and feeds their retraining).
// Views (?view=): parcels (default) · model (precision, learning, proactive
// validation) · settings (mode, source, threshold, where parcels come from).
// List filters in the URL: q, status, flagged=1, alert=open, page, size;
// ?parcel=<id> opens a parcel's detail.
// API: GET /api/parcel-monitor/parcels/list|stats|{id}, /config,
// /analysis/proactive, /model/learning; POST /parcels/{id}/refresh,
// /parcels/{id}/alert/acknowledge|resolve; PUT /config.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  BellRing, BrainCircuit, ChevronRight, CircleCheck, Flag, MessageSquareText, Package, PackageSearch, Plug,
  RefreshCw, Search, Settings2, Target, TriangleAlert, X,
} from 'lucide-react';
import {
  Badge, Banner, Card, DescriptionList, Drawer, EmptyState, Field, Kpi, Meter, Modal, PageHeader, Pagination, Switch, Tabs,
  Timeline, Toast,
} from '../design/ui';
import { formatShortDate, formatShortDateTime } from '../utils/dates';
import './ParcelMonitor.css';

const PAGE_SIZES = [25, 50, 100];
export const STATUS_VALUES = ['pending', 'in_transit', 'delivered', 'returned', 'problem', 'silent_lost'];
// Calibrated operating points of the risk models (see docs/parcel-monitor-spec.md).
const THRESHOLD_OPTIONS = ['0.5', '0.7', '0.85', '0.95'];
const VIEWS = ['parcels', 'model', 'settings'];
export const STATUS_TONE = {
  pending: undefined, in_transit: 'info', delivered: 'success', returned: 'warning', problem: 'danger', silent_lost: 'danger',
};
const RESOLVE_KEYS = {
  false_alarm: 'falseAlarm', resolved_by_action: 'resolvedByAction', confirmed_problem: 'confirmedProblem', other: 'other',
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });
const pct = (value) => (value == null ? '—' : `${Math.round(value * 100)} %`);
// « 28 oct., 14:05 » — the shared short format (utils/dates).
const formatDate = (iso) => formatShortDateTime(iso);

// The risk a parcel carries, from its scores (null when not scored yet).
export const riskLevel = (risk) => {
  if (!risk) return null;
  if (risk.flagged) return { level: 'flagged' };
  const score = Math.max(risk.stall_score || 0, risk.hazard_score || 0);
  if (score >= 0.4) return { level: 'watch', score };
  if (risk.stall_score != null || risk.hazard_score != null) return { level: 'ok', score };
  return { level: 'awaiting' };
};

// « À vérifier »: what needs a person, from what the API returned.
export const parcelAttention = ({ stats, config }) => {
  const items = [];
  if (stats?.models && stats.models.loaded === false) items.push({ key: 'models', tone: 'danger', kind: 'models', detail: stats.models.error });
  if (stats && stats.open_alerts > 0) items.push({ key: 'alerts', tone: 'warning', kind: 'alerts', count: stats.open_alerts });
  if (config && config.tracking_provider === 'edn' && !config.edn_credentials_configured) {
    items.push({ key: 'edn', tone: 'warning', kind: 'ednCredentials' });
  }
  return items;
};

function ParcelMonitor() {
  const { t, i18n } = useTranslation();
  const lang = i18n?.language || 'fr';
  const tp = (key, one, other, options) => t(key, options.count === 1 ? one : other, options);
  const fmt = (n) => Number(n || 0).toLocaleString(lang === 'fr' ? 'fr-FR' : lang);
  const [searchParams, setSearchParams] = useSearchParams();

  // ---- list state (kept in the URL) ----
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
  const size = PAGE_SIZES.includes(parseInt(searchParams.get('size'), 10)) ? parseInt(searchParams.get('size'), 10) : 25;
  const statusFilter = searchParams.get('status') || '';
  const flaggedOnly = searchParams.get('flagged') === '1';
  const alertFilter = searchParams.get('alert') === 'open' ? 'open' : '';
  const urlQuery = searchParams.get('q') || '';
  const view = VIEWS.includes(searchParams.get('view')) ? searchParams.get('view') : 'parcels';
  const openParcelId = searchParams.get('parcel') || '';

  const [searchInput, setSearchInput] = useState(urlQuery);
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(undefined); // undefined = loading, null = unavailable
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(null);
  // Resolve dialog: manual resolution must capture WHY (feeds the precision
  // metrics and the retraining archive).
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolveReason, setResolveReason] = useState('false_alarm');
  const [resolveNote, setResolveNote] = useState('');
  const [proactive, setProactive] = useState(null);
  const [config, setConfig] = useState(null);
  const [configDraft, setConfigDraft] = useState(null);
  const [configDirty, setConfigDirty] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configError, setConfigError] = useState(null);
  const [learning, setLearning] = useState(null);
  const [toast, setToast] = useState(null);
  const detailId = useRef('');

  const updateListParams = useCallback((updates, { replace = false } = {}) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === '' || value === null || value === undefined || value === false) next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    }, { replace });
  }, [setSearchParams]);

  // ---- data loading ----
  const loadList = useCallback(async () => {
    try {
      const params = { page, size };
      if (statusFilter) params.status = statusFilter;
      if (flaggedOnly) params.flagged = true;
      if (alertFilter) params.alert_state = alertFilter;
      if (urlQuery) params.q = urlQuery;
      const res = await axios.get('/api/parcel-monitor/parcels/list', { params, headers: authHeaders() });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Failed to load parcels', error?.response?.status || error?.message);
      setItems([]);
      setTotal(0);
    }
  }, [page, size, statusFilter, flaggedOnly, alertFilter, urlQuery]);

  const loadStats = useCallback(async () => {
    try {
      const res = await axios.get('/api/parcel-monitor/parcels/stats', { headers: authHeaders() });
      setStats(res.data || null);
    } catch (error) {
      console.error('Failed to load parcel stats', error?.response?.status || error?.message);
      setStats(null);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const res = await axios.get('/api/parcel-monitor/config', { headers: authHeaders() });
      setConfig(res.data);
      setConfigDraft({
        enabled: res.data.enabled,
        mode: res.data.mode,
        tracking_provider: res.data.tracking_provider,
        flag_threshold: res.data.flag_threshold === res.data.default_flag_threshold ? '' : String(res.data.flag_threshold),
        external_sync: res.data.external_sync !== false,
        edn_ticket_topic_id: res.data.edn_ticket_topic_id == null ? '' : String(res.data.edn_ticket_topic_id),
      });
      setConfigDirty(false);
    } catch (error) {
      console.error('Failed to load parcel monitor config', error?.response?.status || error?.message);
    }
  }, []);

  const loadProactive = useCallback(async () => {
    try {
      const res = await axios.get('/api/parcel-monitor/analysis/proactive', { headers: authHeaders() });
      setProactive(res.data);
    } catch (error) {
      console.error('Failed to load proactive analysis', error?.response?.status || error?.message);
    }
  }, []);

  const loadLearning = useCallback(async () => {
    try {
      const res = await axios.get('/api/parcel-monitor/model/learning', { headers: authHeaders() });
      setLearning(res.data);
    } catch (error) {
      console.error('Failed to load model learning status', error?.response?.status || error?.message);
    }
  }, []);

  const loadDetail = useCallback(async (parcelId) => {
    detailId.current = parcelId;
    setDetailLoading(true);
    try {
      const res = await axios.get(`/api/parcel-monitor/parcels/${parcelId}`, { headers: authHeaders() });
      if (detailId.current === parcelId) setDetail(res.data);
    } catch (error) {
      console.error('Failed to load parcel detail', error?.response?.status || error?.message);
      if (detailId.current === parcelId) setDetail(null);
    } finally {
      if (detailId.current === parcelId) setDetailLoading(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadStats(); loadConfig(); loadProactive(); loadLearning(); }, [loadStats, loadConfig, loadProactive, loadLearning]);
  useEffect(() => {
    if (openParcelId) loadDetail(openParcelId);
    else { detailId.current = ''; setDetail(null); }
  }, [openParcelId, loadDetail]);

  // Debounced tracking-number search -> URL
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== urlQuery) updateListParams({ q: searchInput, page: '' }, { replace: true });
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput, urlQuery, updateListParams]);

  const refreshAll = () => { loadList(); loadStats(); loadProactive(); loadLearning(); };

  // ---- config actions ----
  const markConfigDirty = (updates) => {
    setConfigDraft((prev) => ({ ...prev, ...updates }));
    setConfigDirty(true);
    setConfigError(null);
  };

  const saveConfig = async () => {
    if (!configDraft) return;
    setConfigSaving(true);
    setConfigError(null);
    try {
      const payload = {
        enabled: configDraft.enabled,
        mode: configDraft.mode,
        tracking_provider: configDraft.tracking_provider,
        flag_threshold: configDraft.flag_threshold === '' ? null : Number(configDraft.flag_threshold),
        external_sync: configDraft.external_sync,
        edn_ticket_topic_id: configDraft.edn_ticket_topic_id === '' ? null : Number(configDraft.edn_ticket_topic_id),
      };
      const res = await axios.put('/api/parcel-monitor/config', payload, { headers: authHeaders() });
      setConfig(res.data);
      setConfigDirty(false);
      setToast({ tone: 'success', text: t('parcelMonitor.configSaved', 'Configuration enregistrée.') });
    } catch (error) {
      const detailText = error.response?.data?.detail;
      setConfigError(typeof detailText === 'string' ? detailText : t('parcelMonitor.configSaveError', 'Impossible d’enregistrer la configuration.'));
    } finally {
      setConfigSaving(false);
    }
  };

  // ---- parcel actions ----
  const refreshParcelTracking = async (parcelId) => {
    setActionBusy(`${parcelId}refresh`);
    try {
      await axios.post(`/api/parcel-monitor/parcels/${parcelId}/refresh`, {}, { headers: authHeaders() });
      // The refresh runs in a background worker; re-read after a short delay.
      setTimeout(async () => {
        try {
          if (detailId.current === parcelId) await loadDetail(parcelId);
          loadList();
        } catch (error) { console.error('Refresh reload failed', error?.message); }
        setActionBusy(null);
      }, 6000);
    } catch (error) {
      console.error('Parcel refresh failed', error?.response?.status || error?.message);
      setToast({ tone: 'danger', text: t('carriersUi.parcels.refreshError', 'Le suivi n’a pas pu être actualisé.') });
      setActionBusy(null);
    }
  };

  const alertAction = async (parcelId, action, body = {}) => {
    setActionBusy(parcelId + action);
    try {
      await axios.post(`/api/parcel-monitor/parcels/${parcelId}/alert/${action}`, body, { headers: authHeaders() });
      await Promise.all([loadList(), loadStats()]);
      if (detailId.current === parcelId) await loadDetail(parcelId);
      setToast({
        tone: 'success',
        text: action === 'resolve'
          ? t('carriersUi.parcels.resolved', 'Alerte résolue.')
          : t('carriersUi.parcels.acknowledged', 'Alerte prise en compte.'),
      });
      return true;
    } catch (error) {
      console.error(`Alert ${action} failed`, error?.response?.status || error?.message);
      const detailText = error.response?.data?.detail;
      setToast({ tone: 'danger', text: typeof detailText === 'string' ? detailText : t('carriersUi.parcels.actionError', 'L’action n’a pas abouti.') });
      return false;
    } finally {
      setActionBusy(null);
    }
  };

  const openResolve = (parcelId) => { setResolveTarget(parcelId); setResolveReason('false_alarm'); setResolveNote(''); };

  // ---- rendering helpers ----
  const statusLabel = (value) => t(`parcelMonitor.status.${value}`, value);
  const statusBadge = (value) => <Badge tone={STATUS_TONE[value]} dot>{statusLabel(value)}</Badge>;

  const riskBadge = (risk) => {
    const level = riskLevel(risk);
    if (!level) return null;
    if (level.level === 'flagged') {
      return <span title={risk.flag_reason || ''}><Badge tone="danger" icon={Flag}>{t('parcelMonitor.risk.flagged', 'À risque')}</Badge></span>;
    }
    if (level.level === 'watch') return <Badge tone="warning">{t('parcelMonitor.risk.watch', 'À surveiller')} {Math.round(level.score * 100)} %</Badge>;
    if (level.level === 'ok') return <Badge tone="success">{t('parcelMonitor.risk.ok', 'Normal')}</Badge>;
    return (
      <span title={t('parcelMonitor.risk.awaitingHint', 'Le score est calculé dès la première remontée de suivi (cycle horaire).')}>
        <Badge>{t('carriersUi.parcels.awaiting', '1er suivi attendu')}</Badge>
      </span>
    );
  };

  const alertBadge = (alert) => {
    if (!alert || alert.state === 'none') return <span className="u-muted">—</span>;
    const labels = {
      open: t('parcelMonitor.alert.open', 'Alerte ouverte'),
      acknowledged: t('parcelMonitor.alert.acknowledged', 'Prise en compte'),
      resolved: t('parcelMonitor.alert.resolved', 'Résolue'),
    };
    const tone = { open: 'danger', acknowledged: 'warning', resolved: 'success' }[alert.state];
    return <Badge tone={tone} dot={alert.state === 'open'}>{labels[alert.state] || alert.state}</Badge>;
  };

  // The alert as the risk cell's second line (the row's action says what to do).
  const alertLine = (alert) => {
    if (!alert || alert.state === 'none') return t('carriersUi.parcels.noAlert', 'aucune alerte');
    return {
      open: t('carriersUi.parcels.alertOpenLine', 'alerte ouverte'),
      acknowledged: t('carriersUi.parcels.alertAckLine', 'alerte prise en compte'),
      resolved: t('carriersUi.parcels.alertResolvedLine', 'alerte résolue'),
    }[alert.state] || alert.state;
  };

  const destination = (parcel) => (parcel.to_city
    ? `${parcel.to_city}${parcel.to_postcode ? ` (${parcel.to_postcode})` : ''}`
    : (parcel.to_postcode || '—'));

  const byStatus = stats?.by_status || {};
  const attention = parcelAttention({ stats, config });
  const anyFilter = Boolean(urlQuery || statusFilter || flaggedOnly || alertFilter);

  // ---- zones ----
  const attentionContent = (item) => ({
    models: [
      t('carriersUi.parcels.attModels', 'Les modèles de risque ne sont pas chargés'),
      item.detail || t('carriersUi.parcels.attModelsHint', 'Aucun colis n’est signalé tant qu’ils ne le sont pas : prévenez le support.'),
      t('carriersUi.parcels.seeModel', 'Voir le modèle'),
      () => updateListParams({ view: 'model' }),
    ],
    alerts: [
      tp('carriersUi.parcels.attAlerts', '{{count}} alerte ouverte attend une décision', '{{count}} alertes ouvertes attendent une décision', { count: item.count }),
      t('carriersUi.parcels.attAlertsHint', 'Prenez-les en compte, ou résolvez-les avec leur motif : ce retour mesure la précision des modèles.'),
      t('carriersUi.parcels.seeAlerts', 'Voir les alertes'),
      () => updateListParams({ view: null, alert: 'open', page: '' }),
    ],
    ednCredentials: [
      t('carriersUi.parcels.attEdn', 'Identifiants API Envoi du Net manquants'),
      t('parcelMonitor.config.ednCredentialsMissing', 'Identifiants API Envoi du Net manquants sur votre compte — contactez le support pour les configurer.'),
      t('carriersUi.parcels.seeSettings', 'Ouvrir les réglages'),
      () => updateListParams({ view: 'settings' }),
    ],
  }[item.kind]);

  const renderAttention = () => {
    if (attention.length === 0) return null;
    return (
      <Card
        icon={TriangleAlert}
        iconTone="warning"
        title={t('carriersUi.attention.title', 'À vérifier')}
        badge={<Badge tone="warning">{attention.length}</Badge>}
        flush
        className="pm-v2__attention"
      >
        <ul className="u-list">
          {attention.map((item) => {
            const [title, detailText, action, onClick] = attentionContent(item);
            return (
              <li className="u-row" key={item.key}>
                <Badge tone={item.tone} dot>
                  {item.tone === 'danger' ? t('carriersUi.attention.blocking', 'Bloquant') : t('carriersUi.attention.watch', 'À surveiller')}
                </Badge>
                <div className="u-row__main">
                  <div className="u-row__title">{title}</div>
                  <div className="u-row__meta u-row__meta--wrap">{detailText}</div>
                </div>
                <div className="u-row__side">
                  <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={onClick}>
                    {action}
                    <ChevronRight aria-hidden="true" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </Card>
    );
  };

  const renderKpis = () => {
    if (stats === undefined) {
      return (
        <div className="u-kpi-row" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton pm-v2__kpi-skeleton" />)}
        </div>
      );
    }
    if (!stats || !stats.total) return null;
    const precision = stats.precision;
    return (
      <div className="u-kpi-row">
        <Kpi
          icon={Package}
          label={t('parcelMonitor.stats.total', 'Colis suivis')}
          value={fmt(stats.total)}
          context={t('carriersUi.parcels.kpiTotal', '{{transit}} en transit · {{delivered}} livrés', { transit: fmt(byStatus.in_transit), delivered: fmt(byStatus.delivered) })}
          active={view === 'parcels' && !anyFilter}
          onClick={() => { setSearchInput(''); updateListParams({ view: null, status: '', flagged: '', alert: '', q: '', page: '' }); }}
        />
        <Kpi
          icon={BellRing}
          tone={stats.open_alerts > 0 ? 'danger' : 'success'}
          label={t('parcelMonitor.stats.openAlerts', 'Alertes ouvertes')}
          value={fmt(stats.open_alerts)}
          context={stats.open_alerts > 0
            ? t('carriersUi.parcels.kpiAlerts', 'à traiter')
            : t('carriersUi.parcels.kpiAlertsNone', 'aucune décision en attente')}
          contextTone={stats.open_alerts > 0 ? 'danger' : undefined}
          active={view === 'parcels' && alertFilter === 'open'}
          onClick={() => updateListParams({ view: null, alert: alertFilter ? '' : 'open', page: '' })}
        />
        <Kpi
          icon={Flag}
          tone="warning"
          label={t('parcelMonitor.stats.flagged', 'Colis signalés')}
          value={fmt(stats.flagged)}
          context={t('carriersUi.parcels.kpiFlagged', '{{share}} des colis suivis', { share: `${((stats.flagged / stats.total) * 100).toLocaleString(lang === 'fr' ? 'fr-FR' : lang, { maximumFractionDigits: 1 })} %` })}
          active={view === 'parcels' && flaggedOnly}
          onClick={() => updateListParams({ view: null, flagged: flaggedOnly ? '' : '1', page: '' })}
        />
        {precision && precision.precision != null && (
          <Kpi
            icon={Target}
            tone={precision.precision >= 0.6 ? 'success' : 'warning'}
            label={t('carriersUi.parcels.kpiPrecision', 'Précision de vos alertes')}
            value={pct(precision.precision)}
            context={t('carriersUi.parcels.kpiPrecisionContext', '{{confirmed}} confirmées sur {{n}} · 30 j', { confirmed: fmt(precision.confirmed), n: fmt(precision.flags_closed) })}
            meter={{ value: Math.round(precision.precision * 100), tone: precision.precision >= 0.6 ? 'success' : 'warning', label: t('carriersUi.parcels.kpiPrecision', 'Précision de vos alertes') }}
            active={view === 'model'}
            onClick={() => updateListParams({ view: 'model' })}
          />
        )}
      </div>
    );
  };

  const renderStatusStrip = () => {
    if (!stats || !stats.total) return null;
    const present = STATUS_VALUES.filter((value) => byStatus[value] > 0);
    return (
      <div className="pm-v2__strip">
        <div className="u-stack" role="img" aria-label={present.map((value) => `${statusLabel(value)} ${byStatus[value]}`).join(', ')}>
          {present.map((value) => (
            <span key={value} className={`pm-v2__seg pm-v2__seg--${value}`} style={{ width: `${(byStatus[value] / stats.total) * 100}%` }} />
          ))}
        </div>
        <div className="pm-v2__legend">
          {present.map((value) => (
            <button
              key={value}
              type="button"
              className={`pm-v2__legend-item${statusFilter === value ? ' is-active' : ''}`}
              aria-pressed={statusFilter === value}
              onClick={() => updateListParams({ status: statusFilter === value ? '' : value, page: '' })}
            >
              <span className={`u-legend__swatch pm-v2__seg--${value}`} aria-hidden="true" />
              {statusLabel(value)}
              <span className="u-num pm-v2__legend-count">{fmt(byStatus[value])}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderRowActions = (parcel) => (
    <span className="pm-v2__row-actions" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
      {parcel.alert?.state === 'open' && (
        <button
          type="button"
          className="u-btn u-btn--secondary u-btn--sm"
          disabled={actionBusy === `${parcel.id}acknowledge`}
          onClick={() => alertAction(parcel.id, 'acknowledge')}
        >
          {t('parcelMonitor.actions.acknowledge', 'Prendre en compte')}
        </button>
      )}
      {['open', 'acknowledged'].includes(parcel.alert?.state) && (
        <button
          type="button"
          className="u-btn u-btn--ghost u-btn--sm"
          disabled={actionBusy === `${parcel.id}resolve`}
          onClick={() => openResolve(parcel.id)}
        >
          {t('parcelMonitor.actions.resolve', 'Résoudre')}
        </button>
      )}
    </span>
  );

  const renderTable = () => {
    if (items === null) {
      return (
        <div className="pm-v2__skeleton" aria-busy="true">
          {Array.from({ length: 6 }, (_, i) => <span key={i} className="u-skeleton" />)}
        </div>
      );
    }
    if (items.length === 0) {
      return anyFilter ? (
        <EmptyState
          icon={Search}
          title={t('carriersUi.parcels.emptyFiltered', 'Aucun colis ne correspond')}
          action={(
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => { setSearchInput(''); updateListParams({ q: '', status: '', flagged: '', alert: '', page: '' }); }}>
              {t('carriersUi.list.clearFilters', 'Effacer les filtres')}
            </button>
          )}
        >
          {t('carriersUi.parcels.emptyFilteredHint', 'Changez la recherche ou les filtres pour voir d’autres colis.')}
        </EmptyState>
      ) : (
        <EmptyState
          icon={PackageSearch}
          title={t('carriersUi.parcels.emptyTitle', 'Aucun colis suivi pour le moment')}
          action={(
            <Link className="u-btn u-btn--secondary u-btn--sm" to="/parcel-monitor?view=settings">
              {t('carriersUi.parcels.seeSettings', 'Ouvrir les réglages')}
            </Link>
          )}
        >
          {t('carriersUi.parcels.emptyHint', 'Les colis arrivent avec vos commandes (API, Shopify, PrestaShop) dès que la surveillance est active.')}
        </EmptyState>
      );
    }
    return (
      <div className="u-table-scroll">
        <table className="u-table pm-v2__table">
          <thead>
            <tr>
              <th style={{ width: '19%' }}>{t('parcelMonitor.columns.tracking', 'N° de suivi')}</th>
              <th style={{ width: '19%' }}>{t('parcelMonitor.columns.carrier', 'Transporteur')}</th>
              <th style={{ width: '18%' }}>{t('parcelMonitor.columns.status', 'Statut')}</th>
              <th style={{ width: '19%' }}>{t('carriersUi.parcels.colRisk', 'Risque · alerte')}</th>
              <th style={{ width: '268px' }} aria-label={t('parcelMonitor.columns.actions', 'Actions')} />
            </tr>
          </thead>
          <tbody>
            {items.map((parcel) => {
              const open = () => updateListParams({ parcel: parcel.id });
              return (
                <tr
                  key={parcel.id}
                  className={`pm-v2__row${parcel.risk?.flagged ? ' pm-v2__row--flagged' : ''}${openParcelId === parcel.id ? ' is-open' : ''}`}
                  tabIndex={0}
                  onClick={open}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
                >
                  <td title={parcel.tracking_number}>
                    <span className="pm-v2__mono">{parcel.tracking_number}</span>
                    {parcel.order_ref && <span className="u-cell-sub">{t('carriersUi.parcels.order', 'Cde {{ref}}', { ref: parcel.order_ref })}</span>}
                  </td>
                  <td title={[parcel.carrier_label || parcel.carrier_key, destination(parcel)].join(' · ')}>
                    {parcel.carrier_label || parcel.carrier_key}
                    <span className="u-cell-sub">{destination(parcel)}</span>
                  </td>
                  <td title={[statusLabel(parcel.status), parcel.provider_status].filter(Boolean).join(' · ')}>
                    <Badge tone={STATUS_TONE[parcel.status]} dot>
                      {parcel.status === 'pending' ? t('carriersUi.parcels.pendingShort', 'En attente') : statusLabel(parcel.status)}
                    </Badge>
                    <span className="u-cell-sub">
                      {parcel.last_event_at ? formatDate(parcel.last_event_at) : t('carriersUi.parcels.noEvent', 'aucun événement')}
                    </span>
                  </td>
                  <td>
                    {riskBadge(parcel.risk)}
                    <span className="u-cell-sub">{alertLine(parcel.alert)}</span>
                  </td>
                  <td>{renderRowActions(parcel)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderParcels = () => (
    <section className="u-card">
      {renderStatusStrip()}
      <div className="u-toolbar">
        <div className="u-input-group u-input-group--lead">
          <Search className="u-input-group__icon" aria-hidden="true" />
          <input
            className="u-input"
            type="search"
            value={searchInput}
            placeholder={t('parcelMonitor.searchPlaceholder', 'Rechercher un numéro de suivi…')}
            aria-label={t('parcelMonitor.searchPlaceholder', 'Rechercher un numéro de suivi…')}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <select
          className="u-select"
          value={statusFilter}
          onChange={(e) => updateListParams({ status: e.target.value, page: '' })}
          aria-label={t('parcelMonitor.columns.status', 'Statut')}
        >
          <option value="">{t('parcelMonitor.filters.allStatuses', 'Tous les statuts')}</option>
          {STATUS_VALUES.map((value) => <option key={value} value={value}>{statusLabel(value)}</option>)}
        </select>
        <Switch
          id="pm-flagged"
          checked={flaggedOnly}
          onChange={(checked) => updateListParams({ flagged: checked ? '1' : '', page: '' })}
          label={t('parcelMonitor.filters.flaggedOnly', 'Colis signalés uniquement')}
        />
        {alertFilter && (
          <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => updateListParams({ alert: '', page: '' })}>
            <X aria-hidden="true" />
            {t('carriersUi.parcels.alertFilter', 'Alertes ouvertes')}
          </button>
        )}
        <span className="u-toolbar__spacer" />
        <select
          className="u-select pm-v2__size"
          value={size}
          onChange={(e) => updateListParams({ size: e.target.value === '25' ? '' : e.target.value, page: '' })}
          aria-label={t('carriersUi.parcels.pageSize', 'Colis par page')}
        >
          {PAGE_SIZES.map((value) => <option key={value} value={value}>{t('carriersUi.parcels.perPage', '{{n}} par page', { n: value })}</option>)}
        </select>
      </div>
      {renderTable()}
      {items && items.length > 0 && (
        <Pagination
          page={page}
          pageSize={size}
          total={total}
          onChange={(next) => updateListParams({ page: next <= 1 ? '' : next })}
          summary={({ first, last, total: count }) => t('carriersUi.pageSummary', '{{first}}–{{last}} sur {{total}}', { first: fmt(first), last: fmt(last), total: fmt(count) })}
          previousLabel={t('carriersUi.previousPage', 'Page précédente')}
          nextLabel={t('carriersUi.nextPage', 'Page suivante')}
        />
      )}
    </section>
  );

  const renderLearning = () => {
    if (!learning) return <span className="u-skeleton pm-v2__card-skeleton" aria-hidden="true" />;
    const platform = learning.precision || {};
    const account = learning.account_precision || {};
    const next = learning.next_training || {};
    const trend = learning.trend || [];
    const model = learning.model || {};
    const percent = Math.round((next.progress || 0) * 1000) / 10;
    return (
      <Card
        icon={BrainCircuit}
        title={t('parcelMonitor.learning.title', 'Apprentissage du modèle')}
        description={t('parcelMonitor.learning.model', 'Modèle {{version}} · entraîné sur {{n}} colis (export externe {{period}})', {
          version: model.version || 'v1', n: fmt(model.trained_on?.shipments), period: model.trained_on?.period || '',
        })}
      >
        <div className="pm-v2__learning">
          <div className="pm-v2__learning-block">
            <span className="u-overline">{t('carriersUi.parcels.platformPrecision', 'Précision · 30 j, tous comptes')}</span>
            <span className="pm-v2__hero u-num">{pct(platform.precision)}</span>
            <span className="u-help">
              {platform.flags_closed
                ? t('parcelMonitor.learning.precisionBasis', '{{confirmed}} alertes confirmées sur {{n}} closes', { confirmed: fmt(platform.confirmed), n: fmt(platform.flags_closed) })
                : t('parcelMonitor.learning.noFlag', 'Aucune alerte close sur la période : la précision s’affiche dès la première.')}
            </span>
            <span className="pm-v2__account">
              {t('parcelMonitor.learning.account', 'Votre compte : {{value}}', { value: pct(account.precision) })}
              {account.flags_closed ? ` (${fmt(account.flags_closed)})` : ''}
            </span>
          </div>
          <div className="pm-v2__learning-block">
            <span className="u-overline">{t('parcelMonitor.learning.trend', 'Évolution hebdomadaire (12 semaines)')}</span>
            <div className="pm-v2__trend" role="img" aria-label={t('parcelMonitor.learning.trend', 'Évolution hebdomadaire (12 semaines)')}>
              {trend.map((point) => (
                <div
                  key={point.week}
                  className="pm-v2__trend-col"
                  title={`${formatShortDate(point.week)} · ${point.precision == null ? t('parcelMonitor.learning.noData', 'pas d’alerte close') : `${pct(point.precision)} (${point.flags_closed})`}`}
                >
                  <div className={`pm-v2__trend-bar${point.precision == null ? ' is-empty' : ''}`} style={{ height: point.precision == null ? '2px' : `${Math.max(4, point.precision * 100)}%` }} />
                </div>
              ))}
            </div>
            <span className="u-help">{t('carriersUi.parcels.trendHelp', 'Part des alertes closes confirmées par le devenir du colis, semaine par semaine.')}</span>
          </div>
          <div className="pm-v2__learning-block">
            <span className="u-overline">{t('parcelMonitor.learning.nextTraining', 'Prochain entraînement')}</span>
            <span className="pm-v2__hero u-num">{`${percent.toLocaleString(lang === 'fr' ? 'fr-FR' : lang)} %`}</span>
            <Meter value={percent} tone={next.ready ? 'success' : undefined} label={t('parcelMonitor.learning.nextTraining', 'Prochain entraînement')} />
            <span className="u-help">
              {next.ready
                ? t('parcelMonitor.learning.ready', 'Prêt : {{n}} colis étiquetés, assez pour réentraîner les modèles.', { n: fmt(next.collected) })
                : t('parcelMonitor.learning.progress', '{{collected}} / {{target}} colis terminés et étiquetés', { collected: fmt(next.collected), target: fmt(next.target) })}
            </span>
            <span className="u-help">
              {t('parcelMonitor.learning.composition', 'dont {{positives}} problèmes réels et {{flags}} alertes closes · tous comptes', { positives: fmt(next.positives), flags: fmt(next.flags_closed) })}
            </span>
          </div>
        </div>
      </Card>
    );
  };

  const renderProactive = () => {
    if (!proactive) return null;
    const totalTickets = proactive.tickets_on_monitored_parcels || 0;
    const parts = [
      ['preempted', proactive.preempted, proactive.median_lead_h != null
        ? t('parcelMonitor.proactive.preemptedWithLead', 'Anticipables (avance médiane {{h}}h)', { h: proactive.median_lead_h })
        : t('parcelMonitor.proactive.preempted', 'Anticipables (signalés avant le ticket)')],
      ['late', proactive.late, t('parcelMonitor.proactive.late', 'Signalés trop tard')],
      ['missed', proactive.missed, t('parcelMonitor.proactive.missed', 'Manqués (aucun signalement)')],
    ];
    return (
      <Card
        icon={Target}
        title={t('parcelMonitor.proactive.title', 'Validation proactive ({{days}}j)', { days: proactive.window_days })}
        description={t('parcelMonitor.proactive.hint', 'Tickets clients réellement ouverts sur des colis suivis, comparés aux signalements du modèle.')}
        footerNote={proactive.preemptable_share != null
          ? t('parcelMonitor.proactive.conclusion', '{{pct}}% des tickets clients auraient pu être ouverts proactivement avec les actions automatiques activées.', { pct: (proactive.preemptable_share * 100).toFixed(0) })
          : null}
      >
        {totalTickets === 0 ? (
          <EmptyState icon={Target} title={t('carriersUi.parcels.proactiveEmpty', 'Aucun ticket client sur un colis suivi')}>
            {t('carriersUi.parcels.proactiveEmptyHint', 'La comparaison apparaît dès qu’un client ouvre un ticket sur un colis surveillé.')}
          </EmptyState>
        ) : (
          <div className="pm-v2__proactive">
            <div className="pm-v2__proactive-total">
              <span className="pm-v2__hero u-num">{fmt(totalTickets)}</span>
              <span className="u-help">{t('parcelMonitor.proactive.tickets', 'Tickets clients sur colis suivis')}</span>
            </div>
            <div className="pm-v2__proactive-split">
              <div className="u-stack" role="img" aria-label={parts.map(([, value, label]) => `${label} ${value}`).join(', ')}>
                {parts.filter(([, value]) => value > 0).map(([key, value]) => (
                  <span key={key} className={`pm-v2__seg pm-v2__seg--${key}`} style={{ width: `${(value / totalTickets) * 100}%` }} />
                ))}
              </div>
              <ul className="pm-v2__proactive-legend">
                {parts.map(([key, value, label]) => (
                  <li key={key}>
                    <span className={`u-legend__swatch pm-v2__seg--${key}`} aria-hidden="true" />
                    <span className="pm-v2__proactive-label">{label}</span>
                    <span className="u-num pm-v2__legend-count">{fmt(value)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </Card>
    );
  };

  const modeHint = {
    alert_only: t('carriersUi.parcels.modeAlertOnlyHint', 'Une alerte s’ouvre ici sur le colis ; rien d’autre n’est fait.'),
    create_ticket: t('parcelMonitor.config.createTicketHint', 'Un ticket est ouvert automatiquement pour le client concerné (résolu via ses alias) et synchronisé vers son système externe si configuré.'),
    auto_contact: t('parcelMonitor.config.autoContactHint', 'L’IA rédige et envoie une demande au transporteur via le canal configuré (portail, email ou formulaire). Si l’automatisation du client est désactivée, le message attend une validation humaine.'),
  };

  const renderSettings = () => {
    if (!config || !configDraft) return <span className="u-skeleton pm-v2__card-skeleton" aria-hidden="true" />;
    return (
      <div className="pm-v2__stack">
        <Card
          icon={Settings2}
          title={t('parcelMonitor.config.title', 'Configuration')}
          badge={config.enabled
            ? <Badge tone="success" dot>{t('carriersUi.parcels.enabled', 'Activée')}</Badge>
            : <Badge dot>{t('carriersUi.parcels.disabled', 'Désactivée')}</Badge>}
          description={t('carriersUi.parcels.settingsDesc', 'Ce que fait la surveillance quand un colis devient à risque.')}
          footerNote={configDirty ? t('parcelMonitor.unsavedChanges', 'Modifications non enregistrées') : null}
          footer={(
            <button type="button" className="u-btn u-btn--primary" disabled={configSaving || !configDirty} onClick={saveConfig}>
              {configSaving ? t('common.saving', 'Enregistrement…') : t('common.save', 'Enregistrer')}
            </button>
          )}
        >
          <div className="u-form">
            {configError && <Banner tone="danger">{configError}</Banner>}
            <Switch
              id="pm-enabled"
              checked={configDraft.enabled}
              onChange={(checked) => markConfigDirty({ enabled: checked })}
              label={t('parcelMonitor.config.enabled', 'Surveillance activée')}
            />
            <div className="u-form-grid">
              <Field
                label={t('parcelMonitor.config.provider', 'Source de suivi')}
                htmlFor="pm-provider"
                error={configDraft.tracking_provider === 'edn' && !config.edn_credentials_configured
                  ? t('parcelMonitor.config.ednCredentialsMissing', 'Identifiants API Envoi du Net manquants sur votre compte — contactez le support pour les configurer.')
                  : undefined}
              >
                <select id="pm-provider" className="u-select" value={configDraft.tracking_provider} onChange={(e) => markConfigDirty({ tracking_provider: e.target.value })}>
                  <option value="edn">{t('parcelMonitor.config.providerEdn', 'Envoi du Net (votre compte)')}</option>
                  <option value="ship24">{t('parcelMonitor.config.providerShip24', 'Ship24')}</option>
                </select>
              </Field>
              <Field
                label={t('parcelMonitor.config.threshold', 'Seuil de signalement')}
                htmlFor="pm-threshold"
                help={t('parcelMonitor.config.thresholdHint', 'Plus le seuil est bas, plus de colis sont signalés (précision plus faible).')}
              >
                <select id="pm-threshold" className="u-select" value={configDraft.flag_threshold} onChange={(e) => markConfigDirty({ flag_threshold: e.target.value })}>
                  <option value="">{t('parcelMonitor.config.thresholdDefault', 'Défaut ({{value}})', { value: config.default_flag_threshold })}</option>
                  {THRESHOLD_OPTIONS.map((value) => <option key={value} value={value}>{value}</option>)}
                  {configDraft.flag_threshold !== '' && !THRESHOLD_OPTIONS.includes(configDraft.flag_threshold) && (
                    <option value={configDraft.flag_threshold}>{configDraft.flag_threshold}</option>
                  )}
                </select>
              </Field>
            </div>
            <Field label={t('parcelMonitor.config.mode', 'Action en cas de risque')} htmlFor="pm-mode" help={modeHint[configDraft.mode]}>
              <select id="pm-mode" className="u-select" value={configDraft.mode} onChange={(e) => markConfigDirty({ mode: e.target.value })}>
                <option value="alert_only">{t('parcelMonitor.config.modeAlertOnly', 'Alerte uniquement')}</option>
                <option value="create_ticket">{t('parcelMonitor.config.modeCreateTicket', 'Alerte + création de ticket')}</option>
                <option value="auto_contact">{t('parcelMonitor.config.modeAutoContact', 'Alerte + ticket + contact transporteur automatique')}</option>
              </select>
            </Field>
            {configDraft.mode !== 'alert_only' && (
              <div className="pm-v2__sync">
                <Switch
                  id="pm-external-sync"
                  checked={configDraft.external_sync}
                  onChange={(checked) => markConfigDirty({ external_sync: checked })}
                  label={t('parcelMonitor.config.externalSync', 'Synchroniser vers le système externe (EDN, Spacefill…)')}
                />
                {configDraft.external_sync && (
                  <Field
                    label={t('parcelMonitor.config.ednTopicId', 'ID de topic EDN pour les tickets créés')}
                    htmlFor="pm-topic"
                    help={t('carriersUi.parcels.topicHelp', 'Requis pour créer le ticket côté Envoi du Net.')}
                  >
                    <input
                      id="pm-topic"
                      type="number"
                      min="1"
                      className="u-input pm-v2__topic"
                      value={configDraft.edn_ticket_topic_id}
                      placeholder={t('carriersUi.parcels.topicPlaceholder', 'ex. 12')}
                      onChange={(e) => markConfigDirty({ edn_ticket_topic_id: e.target.value })}
                    />
                  </Field>
                )}
              </div>
            )}
          </div>
        </Card>

        <Card
          icon={Plug}
          title={t('parcelMonitor.sources.title', 'Arrivée des colis')}
          description={t('parcelMonitor.sources.hint', 'Les colis arrivent avec vos commandes : chaque numéro de suivi d’une commande (API, Shopify, PrestaShop) est surveillé ici dès que la surveillance est active.')}
          flush
        >
          <ul className="u-list">
            <li className="u-row">
              <div className="u-row__main">
                <div className="u-row__title">{t('carriersUi.parcels.shops', 'Boutiques Shopify / PrestaShop')}</div>
                <div className="u-row__meta">{t('carriersUi.parcels.shopsHint', 'Les commandes de vos boutiques connectées apportent leurs numéros de suivi.')}</div>
              </div>
              <div className="u-row__side">
                <Link className="u-btn u-btn--secondary u-btn--sm" to="/commerce/orders?tab=connections">
                  {t('parcelMonitor.sources.shops', 'Connecter Shopify / PrestaShop')}
                  <ChevronRight aria-hidden="true" />
                </Link>
              </div>
            </li>
            <li className="u-row">
              <div className="u-row__main">
                <div className="u-row__title">{t('carriersUi.parcels.api', 'API commandes')}</div>
                <div className="u-row__meta">{t('carriersUi.parcels.apiHint', 'Votre ERP ou WMS envoie ses commandes et leurs colis par l’API.')}</div>
              </div>
              <div className="u-row__side">
                <Link className="u-btn u-btn--secondary u-btn--sm" to="/commerce/orders?tab=api">
                  {t('parcelMonitor.sources.api', 'Clés et documentation de l’API commandes')}
                  <ChevronRight aria-hidden="true" />
                </Link>
              </div>
            </li>
            <li className="u-row">
              <div className="u-row__main">
                <div className="u-row__title">{t('parcelMonitor.config.smsMoved', 'SMS au destinataire')}</div>
                <div className="u-row__meta u-row__meta--wrap">{t('parcelMonitor.config.smsMovedHint', 'Retard détecté, colis reparti, colis livré : les SMS aux destinataires se règlent dans Notifications SMS.')}</div>
              </div>
              <div className="u-row__side">
                <Link className="u-btn u-btn--secondary u-btn--sm" to="/sms-notifications">
                  {t('parcelMonitor.config.smsOpen', 'Ouvrir Notifications SMS')}
                  <ChevronRight aria-hidden="true" />
                </Link>
              </div>
            </li>
          </ul>
        </Card>
      </div>
    );
  };

  const renderDetail = () => {
    const d = detail;
    const closeDrawer = () => updateListParams({ parcel: '' });
    const alertState = d?.alert?.state;
    const canRefresh = d && d.tracking_provider === 'edn' && !['delivered', 'returned'].includes(d.status);
    const scorePct = (value) => `${Math.round(value * 100)} %`;
    const actions = d?.alert?.actions;
    const carrierActionLabel = actions?.carrier ? ({
      contacted: t('parcelMonitor.detail.carrierContacted', 'Demande envoyée'),
      pending_validation: t('parcelMonitor.detail.carrierPending', 'En attente de validation'),
      already_contacted: t('parcelMonitor.detail.carrierAlready', 'Déjà contacté'),
      skipped: actions.carrier.reason || t('parcelMonitor.detail.carrierSkipped', 'Ignoré'),
      error: actions.carrier.reason || t('carriersUi.error', 'Erreur'),
    }[actions.carrier.state] || actions.carrier.state) : null;
    const events = [...(d?.events || [])].reverse();
    return (
      <Drawer
        wide
        icon={Package}
        title={d?.tracking_number || t('carriersUi.parcels.detailTitle', 'Colis')}
        description={d ? [d.carrier_label || d.carrier_key, destination(d)].filter(Boolean).join(' · ') : null}
        onClose={closeDrawer}
        closeLabel={t('common.close', 'Fermer')}
        footer={d && (canRefresh || ['open', 'acknowledged'].includes(alertState)) ? (
          <>
            {canRefresh && (
              <button type="button" className="u-btn u-btn--secondary" disabled={actionBusy === `${d.id}refresh`} onClick={() => refreshParcelTracking(d.id)}>
                <RefreshCw aria-hidden="true" />
                {actionBusy === `${d.id}refresh` ? t('parcelMonitor.detail.refreshing', 'Actualisation…') : t('parcelMonitor.detail.refreshTracking', 'Actualiser le suivi')}
              </button>
            )}
            {['open', 'acknowledged'].includes(alertState) && (
              <button type="button" className="u-btn u-btn--secondary" onClick={() => openResolve(d.id)}>
                {t('parcelMonitor.actions.resolve', 'Résoudre')}
              </button>
            )}
            {alertState === 'open' && (
              <button type="button" className="u-btn u-btn--primary" disabled={actionBusy === `${d.id}acknowledge`} onClick={() => alertAction(d.id, 'acknowledge')}>
                {t('parcelMonitor.actions.acknowledge', 'Prendre en compte')}
              </button>
            )}
          </>
        ) : null}
      >
        {detailLoading && !d && (
          <div className="pm-v2__skeleton" aria-busy="true">
            {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton" />)}
          </div>
        )}
        {!detailLoading && !d && <Banner tone="danger">{t('carriersUi.parcels.detailError', 'Le colis n’a pas pu être chargé.')}</Banner>}
        {d && (
          <div className="pm-v2__detail">
            <div className="pm-v2__detail-badges">
              {statusBadge(d.status)}
              {riskBadge(d.risk)}
              {alertBadge(d.alert)}
            </div>
            {d.risk?.flag_reason && (
              <Banner tone="warning" title={t('parcelMonitor.detail.flagReason', 'Motif du signalement')}>{d.risk.flag_reason}</Banner>
            )}

            <section>
              <h3 className="u-overline pm-v2__detail-title">{t('parcelMonitor.detail.riskBlock', 'Évaluation du risque')}</h3>
              <div className="pm-v2__scores">
                <div>
                  <span className="u-label">{t('parcelMonitor.detail.stallScore', 'Score de ralentissement')}</span>
                  {d.risk?.stall_score != null ? (
                    <span className="pm-v2__score">
                      <span className="u-num">{scorePct(d.risk.stall_score)}</span>
                      <Meter value={d.risk.stall_score * 100} tone={d.risk.stall_score >= 0.4 ? 'warning' : 'success'} label={t('parcelMonitor.detail.stallScore', 'Score de ralentissement')} />
                    </span>
                  ) : <span className="u-help">{t('parcelMonitor.risk.awaitingFirstScan', 'En attente du 1er suivi')}</span>}
                </div>
                <div>
                  <span className="u-label">{t('parcelMonitor.detail.hazardScoreLabel', 'Score de silence')}</span>
                  {d.risk?.hazard_score != null ? (
                    <span className="pm-v2__score">
                      <span className="u-num">{scorePct(d.risk.hazard_score)}{d.risk.checkpoint_h ? ` · ${d.risk.checkpoint_h} h` : ''}</span>
                      <Meter value={d.risk.hazard_score * 100} tone={d.risk.hazard_score >= 0.4 ? 'warning' : 'success'} label={t('parcelMonitor.detail.hazardScoreLabel', 'Score de silence')} />
                    </span>
                  ) : <span className="u-help">{t('parcelMonitor.detail.hazardNotYet', 'Calculé après 6h sans événement')}</span>}
                </div>
              </div>
              <DescriptionList items={[
                d.outcome?.label && {
                  label: t('parcelMonitor.detail.outcome', 'Résultat final'),
                  value: `${t(`parcelMonitor.outcome.${d.outcome.label}`, d.outcome.label)}${d.outcome.false_alarm ? ` — ${t('parcelMonitor.outcome.falseAlarmTag', 'fausse alerte enregistrée')}` : ''}`,
                },
                d.alert?.resolution_reason && {
                  label: t('parcelMonitor.detail.resolutionReason', 'Résolution humaine'),
                  value: `${t(`parcelMonitor.resolve.${RESOLVE_KEYS[d.alert.resolution_reason]}`, d.alert.resolution_reason)}${d.alert.resolution_note ? ` — ${d.alert.resolution_note}` : ''}`,
                },
                d.alert?.sms_sent_at && { label: t('parcelMonitor.detail.smsSent', 'SMS destinataire envoyé'), value: formatDate(d.alert.sms_sent_at) },
              ]}
              />
            </section>

            {actions && (
              <section>
                <h3 className="u-overline pm-v2__detail-title">{t('parcelMonitor.detail.actionsBlock', 'Actions automatiques')}</h3>
                <DescriptionList items={[
                  actions.client?.name && { label: t('parcelMonitor.detail.actionClient', 'Client'), value: actions.client.name },
                  {
                    label: t('parcelMonitor.detail.actionTicket', 'Ticket'),
                    value: actions.ticket_id
                      ? <Link className="u-link" to={`/tickets/${actions.ticket_id}/messages`}>{t('parcelMonitor.detail.openTicket', 'Ouvrir le ticket')}</Link>
                      : <span className="u-muted">—</span>,
                  },
                  actions.external && {
                    label: t('parcelMonitor.detail.actionExternal', 'Système externe'),
                    value: `${(actions.external.integration_key || '').toUpperCase()} — ${actions.external.state === 'created'
                      ? `${t('parcelMonitor.detail.externalCreated', 'ticket créé')}${actions.external.external_ticket_id ? ` #${actions.external.external_ticket_id}` : ''}`
                      : (actions.external.reason || actions.external.state)}`,
                  },
                  carrierActionLabel && { label: t('parcelMonitor.detail.actionCarrier', 'Contact transporteur'), value: carrierActionLabel },
                  actions.error && { label: t('carriersUi.error', 'Erreur'), value: <span className="pm-v2__error">{actions.error}</span> },
                ]}
                />
              </section>
            )}

            <section>
              <h3 className="u-overline pm-v2__detail-title">{t('parcelMonitor.detail.parcelBlock', 'Colis')}</h3>
              <DescriptionList items={[
                d.order_ref && { label: t('parcelMonitor.detail.orderRef', 'Réf. commande'), value: <Link className="u-link" to={`/commerce/orders?q=${encodeURIComponent(d.order_ref)}`}>{d.order_ref}</Link> },
                d.merchant_ref && d.merchant_ref !== 'unknown' && { label: t('parcelMonitor.detail.merchant', 'Expéditeur'), value: d.merchant_ref },
                d.weight_kg != null && { label: t('parcelMonitor.detail.weight', 'Poids'), value: `${d.weight_kg} kg` },
                d.dimensions_cm && {
                  label: t('parcelMonitor.detail.dimensions', 'Dimensions'),
                  value: `${['length_cm', 'width_cm', 'height_cm'].map((k) => d.dimensions_cm[k]).filter((v) => v != null).join(' × ')} cm`,
                },
                d.order_value != null && d.order_value > 0 && { label: t('parcelMonitor.detail.orderValue', 'Valeur'), value: `${d.order_value.toFixed(2)} €` },
                { label: t('parcelMonitor.detail.insurance', 'Assurance'), value: d.insurance ? t('common.yes', 'Oui') : t('common.no', 'Non') },
                d.content && { label: t('parcelMonitor.detail.content', 'Contenu'), value: d.content },
                d.product_category && { label: t('parcelMonitor.detail.productCategory', 'Type de produit'), value: d.product_category },
                d.delivery_mode && { label: t('parcelMonitor.detail.deliveryMode', 'Mode de livraison'), value: d.delivery_mode },
              ]}
              />
            </section>

            <section>
              <h3 className="u-overline pm-v2__detail-title">{t('parcelMonitor.detail.recipientBlock', 'Destinataire')}</h3>
              <DescriptionList items={[
                d.recipient?.name && { label: t('parcelMonitor.detail.recipientName', 'Nom'), value: d.recipient.name },
                d.recipient?.phone && { label: t('parcelMonitor.detail.recipientPhone', 'Téléphone'), value: d.recipient.phone },
                d.recipient?.email && { label: t('parcelMonitor.detail.recipientEmail', 'Email'), value: d.recipient.email },
                d.to_address && {
                  label: t('parcelMonitor.detail.deliveryAddress', 'Adresse de livraison'),
                  value: [d.to_address.company, d.to_address.street, [d.to_address.postcode, d.to_address.city].filter(Boolean).join(' '), d.to_address.country].filter(Boolean).join(', '),
                },
                d.relay_point && {
                  label: t('parcelMonitor.detail.relayPoint', 'Point relais'),
                  value: [d.relay_point.name, d.relay_point.street, [d.relay_point.postcode, d.relay_point.city].filter(Boolean).join(' ')].filter(Boolean).join(', '),
                },
                d.relay_point && {
                  label: t('parcelMonitor.detail.relayPointId', 'ID point relais'),
                  value: d.relay_point.id || <span className="u-muted">{t('parcelMonitor.detail.notProvided', 'Non fourni')}</span>,
                },
              ]}
              />
            </section>

            <section>
              <h3 className="u-overline pm-v2__detail-title">{t('carriersUi.parcels.events', 'Suivi du colis')}</h3>
              {events.length === 0 ? (
                <p className="u-help">{t('parcelMonitor.detail.noEvents', 'Aucun événement de suivi pour le moment.')}</p>
              ) : (
                <Timeline items={events.map((event, index) => {
                  const delivered = event.event_type === 'DEL';
                  const incident = ['INC', 'RET', 'RETD', 'CARD', 'CUSD'].includes(event.event_type);
                  return {
                    key: `${event.event_date}-${index}`,
                    icon: delivered ? CircleCheck : incident ? TriangleAlert : MessageSquareText,
                    tone: delivered ? 'success' : incident ? 'warning' : (index === 0 ? 'accent' : undefined),
                    title: event.status_text,
                    meta: event.event_type,
                    time: formatDate(event.event_date),
                  };
                })}
                />
              )}
            </section>
          </div>
        )}
      </Drawer>
    );
  };

  const subtitle = stats && stats.total
    ? [
      tp('carriersUi.parcels.subtitleTotal', '{{n}} colis suivi', '{{n}} colis suivis', { count: stats.total, n: fmt(stats.total) }),
      tp('carriersUi.parcels.subtitleAlerts', '{{count}} alerte ouverte', '{{count}} alertes ouvertes', { count: stats.open_alerts }),
      config && (config.enabled ? t('carriersUi.parcels.subtitleOn', 'surveillance active') : t('carriersUi.parcels.subtitleOff', 'surveillance désactivée')),
    ].filter(Boolean).join(' · ')
    : t('parcelMonitor.subtitle', 'Tous vos colis suivis en continu : l’IA détecte les colis à risque avant que le destinataire ne s’en aperçoive.');

  return (
    <div className="u-page pm-v2">
      <PageHeader
        title={(
          <span className="pm-v2__title">
            {t('parcelMonitor.title', 'Surveillance des colis')}
            <Badge tone="accent">{t('carriersUi.parcels.beta', 'bêta')}</Badge>
          </span>
        )}
        subtitle={subtitle}
        actions={(
          <>
            <button
              type="button"
              className="u-btn u-btn--secondary u-btn--icon"
              onClick={refreshAll}
              title={t('parcelMonitor.refresh', 'Actualiser')}
              aria-label={t('parcelMonitor.refresh', 'Actualiser')}
            >
              <RefreshCw aria-hidden="true" />
            </button>
          </>
        )}
      />

      <div className="u-well">
        {config && !config.enabled && view !== 'settings' && (
          <Banner
            tone="neutral"
            title={t('carriersUi.parcels.offTitle', 'La surveillance est désactivée')}
            actions={(
              <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => updateListParams({ view: 'settings' })}>
                {t('carriersUi.parcels.seeSettings', 'Ouvrir les réglages')}
              </button>
            )}
          >
            {t('carriersUi.parcels.offHint', 'Aucun nouveau colis n’est analysé et aucune alerte ne s’ouvre.')}
          </Banner>
        )}
        {renderAttention()}
        {renderKpis()}
        <Tabs
          label={t('parcelMonitor.title', 'Surveillance des colis')}
          active={view}
          onChange={(next) => updateListParams({ view: next === 'parcels' ? null : next })}
          tabs={[
            { key: 'parcels', label: t('carriersUi.parcels.tabParcels', 'Colis'), icon: Package, count: items ? fmt(total) : undefined },
            { key: 'model', label: t('carriersUi.parcels.tabModel', 'Modèle et validation'), icon: BrainCircuit },
            { key: 'settings', label: t('carriersUi.parcels.tabSettings', 'Réglages'), icon: Settings2 },
          ]}
        />
        <div role="tabpanel">
          {view === 'parcels' && renderParcels()}
          {view === 'model' && <div className="pm-v2__stack">{renderLearning()}{renderProactive()}</div>}
          {view === 'settings' && renderSettings()}
        </div>
      </div>

      {openParcelId && renderDetail()}

      {/* ---- resolve dialog: reason is mandatory (precision feedback loop) ---- */}
      {resolveTarget && (
        <Modal
          icon={CircleCheck}
          title={t('parcelMonitor.resolve.title', 'Résoudre l’alerte')}
          description={t('parcelMonitor.resolve.hint', 'Indiquez pourquoi : cette information mesure la précision des modèles et alimente leur réentraînement.')}
          onClose={() => setResolveTarget(null)}
          closeLabel={t('common.close', 'Fermer')}
          footer={(
            <>
              <button type="button" className="u-btn u-btn--secondary" onClick={() => setResolveTarget(null)}>
                {t('common.cancel', 'Annuler')}
              </button>
              <button
                type="button"
                className="u-btn u-btn--primary"
                disabled={actionBusy === `${resolveTarget}resolve`}
                onClick={async () => {
                  const ok = await alertAction(resolveTarget, 'resolve', { reason: resolveReason, note: resolveNote || null });
                  if (ok) setResolveTarget(null);
                }}
              >
                {t('parcelMonitor.actions.resolve', 'Résoudre')}
              </button>
            </>
          )}
        >
          <div className="u-form">
            <Field label={t('parcelMonitor.resolve.reason', 'Motif')} htmlFor="pm-resolve-reason">
              <select id="pm-resolve-reason" className="u-select" value={resolveReason} onChange={(e) => setResolveReason(e.target.value)}>
                <option value="false_alarm">{t('parcelMonitor.resolve.falseAlarm', 'Fausse alerte — le colis va bien')}</option>
                <option value="resolved_by_action">{t('parcelMonitor.resolve.resolvedByAction', 'Résolu par notre intervention (transporteur/client contacté)')}</option>
                <option value="confirmed_problem">{t('parcelMonitor.resolve.confirmedProblem', 'Problème confirmé (perdu, endommagé, retourné…)')}</option>
                <option value="other">{t('parcelMonitor.resolve.other', 'Autre')}</option>
              </select>
            </Field>
            <Field label={t('parcelMonitor.resolve.note', 'Note (optionnel)')} htmlFor="pm-resolve-note">
              <textarea
                id="pm-resolve-note"
                className="u-textarea"
                maxLength={500}
                rows={3}
                value={resolveNote}
                placeholder={t('parcelMonitor.resolve.notePlaceholder', 'Contexte utile : ce qui a été constaté ou fait…')}
                onChange={(e) => setResolveNote(e.target.value)}
              />
            </Field>
          </div>
        </Modal>
      )}

      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>
          {toast.text}
        </Toast>
      )}
    </div>
  );
}

export default ParcelMonitor;
